import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Asterisk } from "lucide-react";
import { SITE } from "@/config/site";

export const Logo = ({ light = false }) => (
  <Link to="/" data-testid="nav-logo" aria-label={`${SITE.brand} home`} className="group flex items-center gap-2.5">
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#FF5500] text-white transition-[transform,background-color] duration-500 group-hover:rotate-90 group-hover:bg-[#0055FF]">
      <Asterisk className="h-6 w-6" strokeWidth={3} />
    </span>
    <span className={`font-display text-[1.15rem] font-extrabold tracking-tight sm:text-xl ${light ? "text-white" : "text-slate-900"}`}>
      {SITE.brandParts[0]}<span className={light ? "text-[#FACC15]" : "text-[#0055FF]"}>{SITE.brandParts[1]}</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hover, setHover] = useState(null);
  const { pathname } = useLocation();
  const prefix = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6 sm:pt-4">
      <motion.nav
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        data-testid="main-nav"
        className={`mx-auto flex max-w-[1280px] items-center justify-between rounded-full border border-slate-900/10 px-4 py-2.5 backdrop-blur-md transition-[background-color,box-shadow] duration-300 sm:px-6 ${scrolled ? "bg-white/90 shadow-[0_10px_30px_rgba(15,23,42,0.08)]" : "bg-white/70"}`}
      >
        <Logo />
        <ul className="hidden items-center gap-1 md:flex" onMouseLeave={() => setHover(null)}>
          {SITE.nav.map((n) => (
            <li key={n.href} className="relative" onMouseEnter={() => setHover(n.href)}>
              {hover === n.href && <motion.span layoutId="nav-hover" className="absolute inset-0 rounded-full bg-[#FACC15]" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
              <a data-testid={`nav-link-${n.label.toLowerCase()}`} href={`${prefix}${n.href}`} className="relative rounded-full px-4 py-2 text-sm font-bold text-slate-700 transition-colors hover:text-slate-900">
                {n.label}
              </a>
            </li>
          ))}
        </ul>
        <a data-testid="nav-cta" href={`${prefix}#contact`} className="group hidden items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#FF5500] md:inline-flex">
          Let's Talk <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
        </a>
        <button data-testid="mobile-menu-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen(!open)} className="grid h-10 w-10 place-items-center rounded-full bg-slate-900 text-white md:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </motion.nav>
      <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -12, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          data-testid="mobile-menu" className="mx-auto mt-2 max-w-[1280px] origin-top rounded-3xl border border-slate-900/10 bg-white p-4 shadow-xl md:hidden">
          {SITE.nav.map((n, i) => (
            <motion.a initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }} key={n.href} data-testid={`mobile-nav-link-${n.label.toLowerCase()}`} href={`${prefix}${n.href}`} onClick={() => setOpen(false)} className="block rounded-2xl px-4 py-3 font-display text-lg font-bold text-slate-900 hover:bg-[#FAF9F5]">
              {n.label}
            </motion.a>
          ))}
          <a href={`${prefix}#contact`} onClick={() => setOpen(false)} data-testid="mobile-nav-cta" className="mt-2 block rounded-2xl bg-[#0055FF] px-4 py-3 text-center font-bold text-white">Let's Work Together</a>
        </motion.div>
      )}
      </AnimatePresence>
    </header>
  );
};
